import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Table, Input, Button, Modal, Form, Checkbox, Tooltip, Tag, message } from "antd";
import {
  SearchOutlined, PlusOutlined, EditOutlined,
  FormOutlined, FileTextOutlined, FileExcelOutlined,
  CheckOutlined, CloseOutlined,
} from "@ant-design/icons";
import { apiRequest } from "../../../utilities/apiMethod";
import { useThemeStore } from "../../../store/useThemeStore";
import { colSearch, FormField, actionBtnStyle, exportCSV, exportExcel } from "../../Master/masterUtils";

const INPUT_H = { style: { borderRadius: 8, height: 40 } };
const ENDPOINT = "Forms";

const PERMISSIONS = [
  { key: "canCreate", label: "Create" },
  { key: "canEdit",   label: "Edit"   },
  { key: "canView",   label: "View"   },
  { key: "isVisible", label: "Visible"},
  { key: "isDelete",  label: "Delete" },
  { key: "approve",   label: "Approve"},
  { key: "reject",    label: "Reject" },
  { key: "isExport",  label: "Export" },
];

const BoolIcon = ({ val }) =>
  val
    ? <CheckOutlined style={{ color: "#16a34a", fontSize: 13 }} />
    : <CloseOutlined style={{ color: "#dc2626", fontSize: 13 }} />;

export default function FormsMaster() {
  const { themeColor } = useThemeStore();
  const [data,       setData]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editRec,    setEditRec]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [form]                      = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest("GET", ENDPOINT);
      setData(res?.IsSuccess && Array.isArray(res.Data) ? res.Data : []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(r =>
      r.FormName?.toLowerCase().includes(q) ||
      r.Description?.toLowerCase().includes(q)
    );
  }, [data, search]);

  function openAdd() {
    setEditRec(null);
    form.resetFields();
    form.setFieldsValue({ canCreate: true, canEdit: true, canView: true, isVisible: true, isDelete: true, approve: true, reject: false, isExport: true });
    setModalOpen(true);
  }
  function openEdit(rec) {
    setEditRec(rec);
    form.setFieldsValue({
      formName:    rec.FormName    ?? "",
      description: rec.Description ?? "",
      canCreate:   rec.CanCreate   ?? true,
      canEdit:     rec.CanEdit     ?? true,
      canView:     rec.CanView     ?? true,
      isVisible:   rec.IsVisible   ?? true,
      isDelete:    rec.IsDelete    ?? true,
      approve:     rec.Approve     ?? true,
      reject:      rec.Reject      ?? false,
      isExport:    rec.IsExport    ?? true,
    });
    setModalOpen(true);
  }
  function closeModal() { setModalOpen(false); setSaving(false); }

  async function onFinish(values) {
    setSaving(true);
    try {
      const isEdit  = !!editRec;
      const payload = {
        formName:    values.formName?.trim(),
        description: values.description?.trim() || "",
        canCreate:   !!values.canCreate,
        canEdit:     !!values.canEdit,
        canView:     !!values.canView,
        isVisible:   !!values.isVisible,
        isDelete:    !!values.isDelete,
        approve:     !!values.approve,
        reject:      !!values.reject,
        isExport:    !!values.isExport,
      };
      const endpoint = isEdit ? `${ENDPOINT}/Id?id=${editRec.FormId}` : ENDPOINT;
      const res      = await apiRequest(isEdit ? "PUT" : "POST", endpoint, payload);
      if (res?.IsSuccess) {
        message.success(isEdit ? "Form updated." : "Form created.");
        closeModal(); fetchData();
      } else {
        const errMsg = res?.ErrorMessage?.length > 0 ? res.ErrorMessage.join(", ") : res?.DisplayMessage || "Something went wrong.";
        message.error(errMsg);
      }
    } catch { message.error("Request failed."); }
    finally  { setSaving(false); }
  }

  const columns = [
    {
      title: "S.No", key: "sno", width: 64, align: "center",
      render: (_, __, i) => <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{(pagination.current - 1) * pagination.pageSize + i + 1}</span>,
    },
    {
      title: "Form Name", dataIndex: "FormName", key: "FormName",
      sorter: (a, b) => a.FormName?.localeCompare(b.FormName),
      ...colSearch("FormName", "Form Name", themeColor),
      render: v => <span style={{ fontWeight: 600, color: "#111827" }}>{v}</span>,
    },
    {
      title: "Description", dataIndex: "Description", key: "Description",
      ...colSearch("Description", "Description", themeColor),
      render: v => <span style={{ color: "#374151" }}>{v || "—"}</span>,
    },
    { title: "Create",  dataIndex: "CanCreate", key: "CanCreate",  width: 80,  align: "center", render: v => <BoolIcon val={v} /> },
    { title: "Edit",    dataIndex: "CanEdit",   key: "CanEdit",    width: 70,  align: "center", render: v => <BoolIcon val={v} /> },
    { title: "View",    dataIndex: "CanView",   key: "CanView",    width: 70,  align: "center", render: v => <BoolIcon val={v} /> },
    { title: "Visible", dataIndex: "IsVisible", key: "IsVisible",  width: 80,  align: "center", render: v => <BoolIcon val={v} /> },
    { title: "Delete",  dataIndex: "IsDelete",  key: "IsDelete",   width: 80,  align: "center", render: v => <BoolIcon val={v} /> },
    { title: "Approve", dataIndex: "Approve",   key: "Approve",    width: 85,  align: "center", render: v => <BoolIcon val={v} /> },
    { title: "Reject",  dataIndex: "Reject",    key: "Reject",     width: 80,  align: "center", render: v => <BoolIcon val={v} /> },
    { title: "Export",  dataIndex: "IsExport",  key: "IsExport",   width: 80,  align: "center", render: v => <BoolIcon val={v} /> },
    {
      title: "Action", key: "action", width: 80, align: "center", fixed: "right",
      render: (_, rec) => (
        <Tooltip title="Edit" placement="top">
          <button
            onClick={() => openEdit(rec)}
            style={actionBtnStyle(themeColor)}
            onMouseEnter={e => Object.assign(e.currentTarget.style, { background: "#f0f4ff", borderColor: themeColor + "55" })}
            onMouseLeave={e => Object.assign(e.currentTarget.style, { background: "#f9fafb", borderColor: "#e5e7eb" })}
          >
            <EditOutlined style={{ fontSize: 14 }} />
          </button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ padding: "4px 0" }}>

      {/* Page header */}
      <div style={{
        background: "#fff", borderRadius: 14, border: "1px solid #f0f1f3",
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "12px 18px",
        marginBottom: 10, display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: themeColor, display: "flex", alignItems: "center",
            justifyContent: "center", boxShadow: `0 4px 14px ${themeColor}44`,
            transition: "background 0.3s",
          }}>
            <FormOutlined style={{ fontSize: 17, color: "#fff" }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Form Master</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 3 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: themeColor }}>{data.length}</span>
              <span style={{ fontSize: 13, color: "#6b7280" }}>Total Forms</span>
            </div>
          </div>
        </div>
        <Button
          icon={<PlusOutlined />} onClick={openAdd}
          style={{ height: 38, borderRadius: 9, fontWeight: 600, fontSize: 13, background: themeColor, borderColor: themeColor, color: "#fff", boxShadow: `0 2px 8px ${themeColor}44`, flexShrink: 0, transition: "background 0.3s" }}
        >
          Add Form
        </Button>
      </div>

      {/* Table card */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3", boxShadow: "0 1px 6px rgba(0,0,0,0.04)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #f0f1f3", flexWrap: "wrap", gap: 8 }}>
          <Input
            placeholder="Search form name or description…"
            prefix={<SearchOutlined style={{ color: "#c4c7cd" }} />}
            value={search} onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }}
            allowClear style={{ width: 300, borderRadius: 8, height: 34 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
            <Button icon={<FileTextOutlined />} onClick={() => exportCSV(filtered.map((r, i) => ({ "S.No": i + 1, "Form Name": r.FormName, "Description": r.Description, "Create": r.CanCreate ? "Y" : "N", "Edit": r.CanEdit ? "Y" : "N", "View": r.CanView ? "Y" : "N" })), "form_master.csv")} style={{ height: 32, borderRadius: 8, fontWeight: 600, borderColor: "#22c55e", color: "#16a34a", fontSize: 12 }}>CSV</Button>
            <Button icon={<FileExcelOutlined />} onClick={() => exportExcel(filtered.map((r, i) => ({ "S.No": i + 1, "Form Name": r.FormName, "Description": r.Description, "Create": r.CanCreate ? "Y" : "N", "Edit": r.CanEdit ? "Y" : "N", "View": r.CanView ? "Y" : "N" })), "form_master.xlsx")} style={{ height: 32, borderRadius: 8, fontWeight: 600, borderColor: "#16a34a", color: "#15803d", fontSize: 12 }}>Excel</Button>
          </div>
        </div>
        <Table
          rowKey="FormId"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          size="small"
          scroll={{ x: 1100 }}
          onChange={pag => setPagination({ current: pag.current, pageSize: pag.pageSize })}
          pagination={{
            ...pagination, total: filtered.length,
            showSizeChanger: true, pageSizeOptions: ["10", "20", "50"],
            showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`,
            style: { padding: "8px 14px", margin: 0 },
          }}
        />
      </div>

      {/* Add / Edit modal */}
      <Modal
        open={modalOpen} onCancel={closeModal} footer={null}
        width={520} centered destroyOnClose
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#f4f5f7", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {editRec ? <EditOutlined style={{ fontSize: 14, color: themeColor }} /> : <PlusOutlined style={{ fontSize: 14, color: themeColor }} />}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
                {editRec ? "Edit Form" : "Add Form"}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
                {editRec ? `Editing ${editRec.FormName}` : "Create a new form record"}
              </p>
            </div>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false} style={{ marginTop: 8 }}>
          <FormField name="formName" label="Form Name" rules={[{ required: true, message: "Required" }, { max: 100, message: "Max 100 characters" }]}>
            <Input placeholder="e.g. Country Master" maxLength={100} {...INPUT_H} />
          </FormField>
          <FormField name="description" label="Description" rules={[{ max: 255, message: "Max 255 characters" }]}>
            <Input placeholder="Enter description (optional)" maxLength={255} {...INPUT_H} />
          </FormField>

          {/* Permissions */}
          <div style={{ marginBottom: 20 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.07em" }}>Permissions</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10, padding: "14px 16px", background: "#fafafa", borderRadius: 8, border: "1px solid #f0f1f3" }}>
              {PERMISSIONS.map(({ key, label }) => (
                <Form.Item key={key} name={key} valuePropName="checked" noStyle>
                  <Checkbox>
                    <span style={{ fontSize: 12, color: "#374151", fontWeight: 600 }}>{label}</span>
                  </Checkbox>
                </Form.Item>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, paddingTop: 4, borderTop: "1px solid #f0f1f3" }}>
            <Button onClick={closeModal} style={{ flex: 1, height: 40, borderRadius: 8 }}>Cancel</Button>
            <Button htmlType="submit" loading={saving} style={{ flex: 2, height: 40, borderRadius: 8, fontWeight: 700, background: themeColor, borderColor: themeColor, color: "#fff", transition: "background 0.3s" }}>
              {editRec ? "Save Changes" : "Create Form"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
